import { useRef } from 'react';
import moment from 'moment-timezone';
import { insuredColumn, ListTable, type ListColumn, type ListTableHandle } from '@/components/ListTable';
import { ListSearchControls } from '@/components/ListSearchControls/ListSearchControls';
import { ProvideSearchAPIContext, useSearchApiContext } from '@/components/ListSearchControls/contexts';
import type { SearchOptions } from '@/components/ListSearchControls/types';
import { quoteToneClass, StatusBadge, type QuoteTone } from '@/components/statusBadges';
import { useApi } from '@/lib/legacy';
import { apiHelpers } from '../../utils';
import { QuoteStatuses } from '../../constants';
import { quoteSearchOptions } from '../../constants/searchData/searchOptions';
import * as hooks from '../../hooks';
import { normalizeCamelCase } from '../../ui/utils';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: hooks/useAddProductOptionsToSearchOptions.js
const useAddProductOptionsToSearchOptions = (options: unknown): SearchOptions => (hooks as any).useAddProductOptionsToSearchOptions(options);

export type Quote = {
  id: string;
  quoteNumber?: number | string;
  insured?: string;
  status?: string;
  product?: { name?: string; productType?: string } | null;
  createdAt?: string | number;
  lastChangeAt?: string | number | null;
  coverageAt?: string | number | null;
};

type Chip = { text: string; tone: QuoteTone };

const STAGE_CHIPS: Record<string, Chip> = {
  [QuoteStatuses.pending]: { text: 'Pending', tone: 'pending' },
  [QuoteStatuses.released]: { text: 'Released', tone: 'released' },
  [QuoteStatuses.prebound]: { text: 'Prebound', tone: 'prebound' },
  [QuoteStatuses.bound]: { text: 'Bound', tone: 'bound' },
};

/** The quote's status chip, as the legacy list's useFormatQuoteData chose its first chip. */
export const quoteStatusChip = ({ status, createdAt }: Quote): Chip | null => {
  const { text: expirationText } = apiHelpers.getQuoteExpiration({ createdAt, status });
  if (status === QuoteStatuses.rejected) return { text: 'Rejected', tone: 'rejected' };
  if (status === QuoteStatuses.expired) return { text: expirationText, tone: 'expired' };
  const stage = status ? STAGE_CHIPS[status] : undefined;
  // A quote at no known stage shows only its expiration.
  return stage ?? { text: expirationText, tone: 'none' };
};

const date = (value?: string | number | null) => (value ? moment.utc(value).format('MM-DD-YYYY') : null);
const quotePath = (quote: Quote) => `/quotes/${quote.id}`;

// Today's table-mode columns (tableColumnsConfig), fixed at each switchable column's default. The
// widths (D36) add up to 796px, which fits the list at 1280px beside a classic 17px scrollbar, the
// insured taking the widest share.
export const quoteColumns: ListColumn<Quote>[] = [
  {
    id: 'quoteNumber',
    header: 'NO.',
    size: 70,
    accessorFn: ({ quoteNumber }) => `#${quoteNumber}`,
    cell: ({ getValue }) => <span className="font-bold">{getValue<string>()}</span>,
  },
  {
    id: 'lastChangeAt',
    header: 'LAST UPDATED',
    size: 116,
    accessorFn: ({ lastChangeAt, createdAt }) => date(lastChangeAt) ?? date(createdAt) ?? 'N/A',
  },
  insuredColumn<Quote>({ header: 'INSURED', insured: (quote) => quote.insured, text: (quote) => quote.insured || 'N/A', path: quotePath, size: 213 }),
  {
    id: 'product',
    header: 'PRODUCT',
    size: 150,
    accessorFn: ({ product }) => (product ? apiHelpers.resolveProduct(product).labels.name : 'N/A'),
  },
  {
    id: 'coverageAt',
    header: 'COVERAGE START',
    size: 135,
    accessorFn: ({ coverageAt }) => date(coverageAt) ?? 'N/A',
    cell: ({ getValue }) => <span className="font-bold">{getValue<string>()}</span>,
  },
  {
    id: 'status',
    header: 'STATUS',
    size: 112,
    accessorFn: (quote) => quoteStatusChip(quote)?.text ?? '',
    cell: ({ row }) => {
      const chip = quoteStatusChip(row.original);
      // An empty chip (no status, no creation date) shows nothing: normalizeCamelCase can't take ''.
      return chip?.text ? <StatusBadge className={quoteToneClass(chip.tone)}>{normalizeCamelCase(chip.text)}</StatusBadge> : null;
    },
  },
];

/**
 * The Quotes list: the shared search toolbar over one table of quotes, as on Policies (D35). Search,
 * filters and sort live in the URL and the QuotesListView search settings, as before.
 */
export const QuotesListView = ({ searchPath }: { searchPath?: string }) => {
  const listRef = useRef<ListTableHandle>(null);
  const { getQuotes } = useApi();
  const searchOptions = useAddProductOptionsToSearchOptions(quoteSearchOptions);

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-white px-4 pt-16 pb-6 md:px-[120px]">
      <ProvideSearchAPIContext
        {...(searchPath !== undefined ? { searchPath } : {})}
        searchOptions={searchOptions}
        getData={getQuotes as (...args: unknown[]) => Promise<unknown>}
        appSettingsKey="QuotesListView"
        cacheKey="quotelist"
        compareWithout="url"
        keepRowsOnFailedPage
        listRef={listRef}
      >
        <div className="pb-4">
          <ListSearchControls heading="Quotes" searchPlaceholder="Search for quotes" showDisplayTypeToggle={false} />
        </div>
        <Quotes tableRef={listRef} />
      </ProvideSearchAPIContext>
    </div>
  );
};

const Quotes = ({ tableRef }: { tableRef: React.RefObject<ListTableHandle> }) => {
  const { data, loading, loadingFromCache, requestingMore, hasMore, failed, requestMoreData } = useSearchApiContext();
  return (
    <ListTable<Quote>
      ref={tableRef}
      rows={data as Quote[]}
      columns={quoteColumns}
      rowPath={quotePath}
      rowId={(quote) => String(quote.id)}
      label="Quotes"
      emptyMessage="No quotes to show"
      loading={loading || requestingMore}
      fetching={loading || !!loadingFromCache || requestingMore}
      failed={failed}
      hasMore={hasMore}
      onLoadMore={requestMoreData}
    />
  );
};

export default QuotesListView;
